import _JSON$stringify from "babel-runtime/core-js/json/stringify";
import _extends from "babel-runtime/helpers/extends";
import _regeneratorRuntime from "babel-runtime/regenerator";
/*  globals window, sonic_loader, sonic */

import { eventChannel, END } from "redux-saga";
import { delay, take, cancelled, select, takeEvery, takeLatest, put, call, fork } from "redux-saga/effects";
import { selectors, actions } from "./srereducer";
import { cancelableSaga, takeFirst, extractPath } from "@rosetta/redux-utils";
import { SrePlaySound, SreReadingTracker } from "./sessions";
import uuidV4 from "uuid/v4";

var DEFAULT_DIFFICULTY = 3;
var POST_END_WAIT_MS = 100;

/**
 * Creates a set of sagas that manage the SRE interactions.
 *
 * @param appSelectors - Your app specific selectors, you must provide these two:
 *     function getCurrentProductLanguage(state)
 *     function getSpeechVoiceType(state)
 *     These will get called in initializeSRESaga which is triggered by INIT_SRE / actions.initSRE() so make
 *     sure they have valid SRE values by then.
 *
 * @param cancelListenActions - List of strings for redux actions that should cancel a listen session
 * @param logger - inject a logger with same API as console for custom logging; defaults to console
 * @return {sreSaga}
 */
function createSRESaga(appSelectors, cancelListenActions) {
  var _marked = /*#__PURE__*/_regeneratorRuntime.mark(enableAudioLog),
      _marked2 = /*#__PURE__*/_regeneratorRuntime.mark(refreshMicList),
      _marked3 = /*#__PURE__*/_regeneratorRuntime.mark(loadSonicLib),
      _marked4 = /*#__PURE__*/_regeneratorRuntime.mark(clearCurrentSession),
      _marked5 = /*#__PURE__*/_regeneratorRuntime.mark(loadModelSession),
      _marked6 = /*#__PURE__*/_regeneratorRuntime.mark(checkMicAccessSaga),
      _marked7 = /*#__PURE__*/_regeneratorRuntime.mark(runCalibrateSession),
      _marked8 = /*#__PURE__*/_regeneratorRuntime.mark(calibrateSaga),
      _marked9 = /*#__PURE__*/_regeneratorRuntime.mark(listenSession),
      _marked10 = /*#__PURE__*/_regeneratorRuntime.mark(listenForPhraseSaga),
      _marked11 = /*#__PURE__*/_regeneratorRuntime.mark(playSoundSessionSaga),
      _marked12 = /*#__PURE__*/_regeneratorRuntime.mark(whileReadingTrackerSessionIsRunning),
      _marked13 = /*#__PURE__*/_regeneratorRuntime.mark(readingTrackerSagaV2),
      _marked14 = /*#__PURE__*/_regeneratorRuntime.mark(readingTrackerPlaybackSagaV2),
      _marked15 = /*#__PURE__*/_regeneratorRuntime.mark(handleReadingTrackerPlaybackEvent),
      _marked16 = /*#__PURE__*/_regeneratorRuntime.mark(ensureSpeechEngineAndMicrophone),
      _marked17 = /*#__PURE__*/_regeneratorRuntime.mark(seekTrackerPlaybackSessionTime),
      _marked18 = /*#__PURE__*/_regeneratorRuntime.mark(installMicSaga),
      _marked19 = /*#__PURE__*/_regeneratorRuntime.mark(setCurrentMic),
      _marked20 = /*#__PURE__*/_regeneratorRuntime.mark(pollForMicAccess),
      _marked21 = /*#__PURE__*/_regeneratorRuntime.mark(micAccessFlow),
      _marked22 = /*#__PURE__*/_regeneratorRuntime.mark(initializeSonic),
      _marked23 = /*#__PURE__*/_regeneratorRuntime.mark(initializeSpeechModel),
      _marked24 = /*#__PURE__*/_regeneratorRuntime.mark(initializeSRESaga),
      _marked25 = /*#__PURE__*/_regeneratorRuntime.mark(cancelSessionSaga),
      _marked26 = /*#__PURE__*/_regeneratorRuntime.mark(sreSaga);

  var logger = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : console;

  cancelListenActions = cancelListenActions.concat(actions.SRE_ABORT_LISTEN); // Add this SRE specific one in

  var speechEngine = void 0;
  var currentSession = void 0;
  var currentSessionId = void 0;
  var nextSessionId = null;
  var currentSessionInterrupting = false;
  var loading = void 0;

  var sonicEvents = window.sonic_events;

  function validateSpeechEngine(method) {
    if (!speechEngine) {
      logger.warn("Speech engine not initialized yet", method);
    }
  }

  /**
   * Enables audio log support
   */
  // eslint-disable-next-line require-yield
  function enableAudioLog(action) {
    return _regeneratorRuntime.wrap(function enableAudioLog$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (action.payload.viperUser) {
              speechEngine.setSoundLogSaveLocation(sonic.SpeechEngine.getREMOTE_NO_AUDIO());
            } else {
              speechEngine.setSoundLogSaveLocation(sonic.SpeechEngine.getREMOTE());
            }
            speechEngine.setSoundLogIdentifiers(action.payload.appId, action.payload.userId);
            logger.info("Sound log recording enabled.");

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _marked, this);
  }

  /**
   * The SRE relies on JQuery style events.
   * Here's a helper function that takes some object and binds types of events to it; start, status, and an end, and
   * return an eventChannel corresponding to those.
   *
   * @param sreobject an object to call jQuery(sreobject).bind on
   * @param statusEvent an event to listen to and yield values from
   * @param endEvent an event which tells us we are completed.
   * @param errorEvent an event to bound to the sre end event process.
   * @param startEvent an event to bound to the sre start event process.
   * @param interruptingEvent an event to bound to the sre interrupting event process.
   * @returns {Channel<T>}
   */
  function createEventChannel(sreobject, statusEvent, endEvent) {
    var errorEvent = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    var startEvent = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : null;
    var interruptingEvent = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : null;

    var body = function body(emitter) {
      var eventEmitter = sonicEvents(sreobject);
      var onStart = function onStart() {
        for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        return emitter({ type: "start", args: args });
      };
      var onStatus = function onStatus() {
        for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          args[_key2] = arguments[_key2];
        }

        return emitter({ type: "status", args: args });
      };
      var onError = function onError() {
        for (var _len3 = arguments.length, args = Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
          args[_key3] = arguments[_key3];
        }

        return emitter({ type: "error", args: args });
      };
      var onInterrupting = function onInterrupting() {
        for (var _len4 = arguments.length, args = Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
          args[_key4] = arguments[_key4];
        }

        currentSessionInterrupting = true;
        emitter({ type: "interrupting", args: args });
      };
      var onEnd = function onEnd() {
        for (var _len5 = arguments.length, args = Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
          args[_key5] = arguments[_key5];
        }

        setTimeout(function () {
          currentSessionInterrupting = false;
        }, POST_END_WAIT_MS);
        emitter({ type: "end", args: args });
        // take(END) will cause the saga to terminate by jumping to the finally block
        emitter(END);
      };

      interruptingEvent && eventEmitter.bind(interruptingEvent, onInterrupting);
      startEvent && eventEmitter.bind(startEvent, onStart);
      statusEvent && eventEmitter.bind(statusEvent, onStatus);
      endEvent && eventEmitter.bind(endEvent, onEnd);
      errorEvent && eventEmitter.bind(errorEvent, onError);

      var unsubscribe = function unsubscribe() {
        interruptingEvent && eventEmitter.unbind(interruptingEvent, onInterrupting);
        startEvent && eventEmitter.unbind(startEvent, onStart);
        statusEvent && eventEmitter.unbind(statusEvent, onStatus);
        endEvent && eventEmitter.unbind(endEvent, onEnd);
        errorEvent && eventEmitter.unbind(errorEvent, onError);
      };
      return unsubscribe;
    };

    return eventChannel(body);
  }

  /**
   * Refreshes the mic list in the redux store
   */
  function refreshMicList() {
    var audioEnumerator, channel, list;
    return _regeneratorRuntime.wrap(function refreshMicList$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            audioEnumerator = speechEngine.getAudioEnumerator();
            channel = createEventChannel(audioEnumerator, null, "ready", null);

            audioEnumerator.refreshDevices();
            _context2.next = 5;
            return take(channel);

          case 5:
            list = audioEnumerator.getInputDevices();
            _context2.next = 8;
            return put(actions.listMics(list));

          case 8:
          case "end":
            return _context2.stop();
        }
      }
    }, _marked2, this);
  }

  /** Loads the sonic library.
   *  Will yield onSRELoadStatus actions to update store of loading percentage.
   */
  function loadSonicLib() {
    var srePath = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "/sre/";
    var sonicWorkerUrl = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var channel, event, progress, percent;
    return _regeneratorRuntime.wrap(function loadSonicLib$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            logger.log("ReactSRE: Attempting to load SRE from", srePath, sonicWorkerUrl, sonic);

            channel = createEventChannel(sonic, "progress", "ready", "error");

            sonicWorkerUrl = sonicWorkerUrl || srePath + "sonic_worker.js";

            logger.log("ReactSRE: Beginning libsonic download");
            sonic.load({ assetUrl: srePath, workerUrl: sonicWorkerUrl });
            logger.log("ReactSRE: Waiting for load to finish.", srePath);

            _context3.prev = 6;

          case 7:
            if (!true) {
              _context3.next = 23;
              break;
            }

            _context3.next = 10;
            return take(channel);

          case 10:
            event = _context3.sent;

            if (!(event.type && event.type === "status")) {
              _context3.next = 16;
              break;
            }

            progress = event.args[1];
            percent = Math.round(progress * 100);
            _context3.next = 16;
            return put(actions.onSRELoadStatus(percent));

          case 16:
            if (!(event.type && event.type === "error")) {
              _context3.next = 21;
              break;
            }

            _context3.next = 19;
            return put(actions.onSRELoadFailure(event));

          case 19:
            logger.error("ReactSRE: Could not load SRE");
            return _context3.abrupt("return", null);

          case 21:
            _context3.next = 7;
            break;

          case 23:
            _context3.next = 30;
            break;

          case 25:
            _context3.prev = 25;
            _context3.t0 = _context3["catch"](6);

            logger.error("ReactSRE: Failed while loading sonic.");
            _context3.next = 30;
            return put(actions.onSRELoadFailure(_context3.t0));

          case 30:
            _context3.prev = 30;

            logger.log("ReactSRE: done loading sonic lib");
            return _context3.finish(30);

          case 33:
          case "end":
            return _context3.stop();
        }
      }
    }, _marked3, this, [[6, 25, 30, 33]]);
  }

  /**
   * Initialize and return the speech engine.
   */
  function createSpeechEngine() {
    var identifier = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "Callisto";

    var speechEngine = sonic.createPlatformSpeechEngine();
    speechEngine.getSpeechModelProvider().setIdentifier(identifier);
    return speechEngine;
  }

  /**
   * clearCurrentSession()
   * stop any currently running session and wait until it ends
   */
  function clearCurrentSession(newSessionId) {
    return _regeneratorRuntime.wrap(function clearCurrentSession$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            // any sessions calling clearCurrentSession claim the right to be the next session started
            nextSessionId = newSessionId;

            // if already interrupting just wait for interruption to end

            if (!currentSessionInterrupting) {
              _context4.next = 8;
              break;
            }

          case 2:
            if (!currentSessionInterrupting) {
              _context4.next = 7;
              break;
            }

            _context4.next = 5;
            return delay(5);

          case 5:
            _context4.next = 2;
            break;

          case 7:
            return _context4.abrupt("return", null);

          case 8:
            if (!(currentSession && currentSession.isRunning())) {
              _context4.next = 22;
              break;
            }

            _context4.prev = 9;

            // Session has issues starting immediately after being interrupted.
            //  Wait until previous session ends to start new
            currentSessionInterrupting = true;
            currentSession.interrupt();

          case 12:
            if (!currentSession.isRunning()) {
              _context4.next = 17;
              break;
            }

            _context4.next = 15;
            return delay(5);

          case 15:
            _context4.next = 12;
            break;

          case 17:
            _context4.prev = 17;
            _context4.next = 20;
            return delay(POST_END_WAIT_MS);

          case 20:
            currentSessionInterrupting = false;
            return _context4.finish(17);

          case 22:
          case "end":
            return _context4.stop();
        }
      }
    }, _marked4, this, [[9,, 17, 22]]);
  }

  /**
   * Loads a speech model.
   * Yields onSRELoadStatus actions while it does.
   */
  function loadModelSession(speechEngine) {
    var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "en-US";
    var voiceType = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "male";
    var difficulty = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : DEFAULT_DIFFICULTY;
    var newSessionId, loadModelSession, channel, progress, percent;
    return _regeneratorRuntime.wrap(function loadModelSession$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            newSessionId = uuidV4();
            _context5.next = 3;
            return clearCurrentSession(newSessionId);

          case 3:
            if (!(nextSessionId !== newSessionId)) {
              _context5.next = 5;
              break;
            }

            return _context5.abrupt("return", null);

          case 5:
            loadModelSession = speechEngine.createLoadModelSession({
              language: language,
              voiceType: voiceType
            }, difficulty);

            setCurrentSession(loadModelSession, "loadModelSession_" + newSessionId);
            channel = createEventChannel(loadModelSession, "update", "end", "error");

            loadModelSession.run();
            _context5.prev = 9;

          case 10:
            if (!true) {
              _context5.next = 19;
              break;
            }

            _context5.next = 13;
            return take(channel);

          case 13:
            progress = loadModelSession.getProgress();
            percent = Math.round(progress * 100);
            _context5.next = 17;
            return put(actions.onSRELoadStatus(percent));

          case 17:
            _context5.next = 10;
            break;

          case 19:
            _context5.prev = 19;

            logger.log("ReactSRE: done loading speech model");
            return _context5.finish(19);

          case 22:
          case "end":
            return _context5.stop();
        }
      }
    }, _marked5, this, [[9,, 19, 22]]);
  }

  /**
   * Runs a checkMicAccess session in the SRE.
   * Puts a micAccess action to update the store with results when it's done.
   */
  function checkMicAccessSaga() {
    var newSessionId, checkAccessSession, channel, status;
    return _regeneratorRuntime.wrap(function checkMicAccessSaga$(_context6) {
      while (1) {
        switch (_context6.prev = _context6.next) {
          case 0:
            newSessionId = uuidV4();
            _context6.next = 3;
            return clearCurrentSession(newSessionId);

          case 3:
            if (!(nextSessionId !== newSessionId)) {
              _context6.next = 5;
              break;
            }

            return _context6.abrupt("return", null);

          case 5:
            validateSpeechEngine("checkMicAccessSaga");
            checkAccessSession = speechEngine.createCheckMicAccessSession();
            channel = createEventChannel(checkAccessSession, null, "end");

            setCurrentSession(checkAccessSession, "checkAccessSession_" + newSessionId);
            checkAccessSession.run();
            _context6.prev = 10;

          case 11:
            if (!true) {
              _context6.next = 16;
              break;
            }

            _context6.next = 14;
            return take(channel);

          case 14:
            _context6.next = 11;
            break;

          case 16:
            _context6.prev = 16;
            status = checkAccessSession.getAccessStatus();
            _context6.next = 20;
            return clearCurrentSession();

          case 20:
            _context6.next = 22;
            return put(actions.micAccess(status));

          case 22:
            logger.log("ReactSRE: \t mic access   : " + status);
            return _context6.abrupt("return", status);

          case 25:
          case "end":
            return _context6.stop();
        }
      }
    }, _marked6, this, [[10,, 16, 25]]);
  }

  /**
   * Runs the actual microphone calibration session, called by the calibrate saga.
   *
   * Puts sreAudioLevel actions to update the audio level
   * Yields the session when done.
   *
   * @param calibrateSession
   */
  function runCalibrateSession(calibrateSession) {
    var channel, volume, _ref, type, storePlaySoundSessionSoundObject, soundObj, energy;

    return _regeneratorRuntime.wrap(function runCalibrateSession$(_context7) {
      while (1) {
        switch (_context7.prev = _context7.next) {
          case 0:
            channel = createEventChannel(calibrateSession, "update", "end");

            calibrateSession.withMetadata(sonic.globals.getAUDIO_METADATA_ENERGY());

            if (!appSelectors.getSoundVolume) {
              _context7.next = 8;
              break;
            }

            _context7.next = 5;
            return select(appSelectors.getSoundVolume);

          case 5:
            _context7.t0 = _context7.sent;
            _context7.next = 9;
            break;

          case 8:
            _context7.t0 = 1;

          case 9:
            volume = _context7.t0;

            calibrateSession.withDefaultPromptSound(volume);
            calibrateSession.run();

            _context7.prev = 12;

          case 13:
            if (!true) {
              _context7.next = 29;
              break;
            }

            _context7.next = 16;
            return take(channel);

          case 16:
            _ref = _context7.sent;
            type = _ref.type;

            if (!(type === "end")) {
              _context7.next = 24;
              break;
            }

            storePlaySoundSessionSoundObject = SrePlaySound.actions.storePlaySoundSessionSoundObject;
            soundObj = calibrateSession.detachSoundObject();

            if (!soundObj) {
              _context7.next = 24;
              break;
            }

            _context7.next = 24;
            return put(storePlaySoundSessionSoundObject(soundObj, calibrateSession.getSoundLog()));

          case 24:

            // Because calibrateSession.withMetadata isn't working, this fails:
            energy = calibrateSession.getMeanFrameEnergy();
            _context7.next = 27;
            return put(actions.sreAudioLevel(energy));

          case 27:
            _context7.next = 13;
            break;

          case 29:
            _context7.prev = 29;
            _context7.next = 32;
            return calibrateSession;

          case 32:
            return _context7.finish(29);

          case 33:
          case "end":
            return _context7.stop();
        }
      }
    }, _marked7, this, [[12,, 29, 33]]);
  }

  /**
   * Saga to run the full calibration session and record the results in the store.
   *
   * @param action
   */
  function calibrateSaga(action) {
    var newSessionId, calibrateSession, qualityStr, quality, error;
    return _regeneratorRuntime.wrap(function calibrateSaga$(_context8) {
      while (1) {
        switch (_context8.prev = _context8.next) {
          case 0:
            if (speechEngine) {
              _context8.next = 4;
              break;
            }

            logger.warn("ReactSRE: tried to listen without initializing the SRE first. This is going to take a bit.");
            _context8.next = 4;
            return initializeSRESaga(action);

          case 4:
            newSessionId = uuidV4();
            _context8.next = 7;
            return clearCurrentSession(newSessionId);

          case 7:
            if (!(nextSessionId !== newSessionId)) {
              _context8.next = 9;
              break;
            }

            return _context8.abrupt("return", null);

          case 9:
            calibrateSession = speechEngine.createCalibrateSession();

            setCurrentSession(calibrateSession, "calibrateSession_" + newSessionId);
            _context8.next = 13;
            return runCalibrateSession(calibrateSession);

          case 13:
            qualityStr = "";

            if (!(calibrateSession.getState() === sonic.Task.getSUCCEEDED())) {
              _context8.next = 19;
              break;
            }

            quality = calibrateSession.getAudioQuality().getCalibration_result();

            if (quality === sonic.globals.getCALIBRATION_RESULT_OK()) {
              qualityStr = "ok";
            } else if (quality === sonic.globals.getCALIBRATION_RESULT_TOO_LOUD()) {
              qualityStr = "too loud";
            } else if (quality === sonic.globals.getCALIBRATION_RESULT_TOO_SOFT()) {
              qualityStr = "too soft";
            } else if (quality === sonic.globals.getCALIBRATION_RESULT_NO_SIGNAL()) {
              qualityStr = "silence";
            }
            _context8.next = 23;
            break;

          case 19:
            qualityStr = "failed";
            error = {
              qualityStr: qualityStr,
              code: calibrateSession.getErrorCode(),
              message: calibrateSession.getErrorMessage(),
              micAccessStatus: calibrateSession.getMicAccessStatus(),
              sessionData: _extends({}, calibrateSession)
            };
            _context8.next = 23;
            return put(actions.calibrateError(error));

          case 23:
            _context8.next = 25;
            return put(actions.calibrateComplete(qualityStr));

          case 25:
          case "end":
            return _context8.stop();
        }
      }
    }, _marked8, this);
  }

  /**
   * Runs the actual listening session, called by the listenForPhrase saga.
   *
   * @param phraseSession
   */
  function listenSession(phraseSession) {
    var channel, result;
    return _regeneratorRuntime.wrap(function listenSession$(_context9) {
      while (1) {
        switch (_context9.prev = _context9.next) {
          case 0:
            channel = createEventChannel(phraseSession, "update", "end");
            _context9.prev = 1;

          case 2:
            if (!true) {
              _context9.next = 17;
              break;
            }

            _context9.next = 5;
            return take(channel);

          case 5:
            if (!phraseSession.getUpdateResult) {
              _context9.next = 13;
              break;
            }

            // newer versions of sonic use getUpdateResult instead of getHypothesis
            result = phraseSession.getUpdateResult();
            // Store session update results, this includes updating the hypothesis

            _context9.t0 = result;

            if (!_context9.t0) {
              _context9.next = 11;
              break;
            }

            _context9.next = 11;
            return put(actions.sreListenForPhrasesUpdate(result));

          case 11:
            _context9.next = 15;
            break;

          case 13:
            _context9.next = 15;
            return put(actions.sreHypothesis(phraseSession.getHypothesis().getResponse()));

          case 15:
            _context9.next = 2;
            break;

          case 17:
            _context9.prev = 17;
            _context9.next = 20;
            return phraseSession;

          case 20:
            return _context9.finish(17);

          case 21:
          case "end":
            return _context9.stop();
        }
      }
    }, _marked9, this, [[1,, 17, 21]]);
  }

  /**
   * Saga that listens for a phrase and then puts the result into the store.
   *
   * @param action
   */
  function listenForPhraseSaga(action) {
    var _action$payload, phrases, promptId, duration, useLFOP, isFake, newSessionId, phraseSession, volume, audioQualityMetrics, calibrationResult, silence, tooLoud, tooSoft, snrEstimate, tooMuchBackgroundNoise, hypothesis, grading, detectedPhrase, passed, result, phraseListeningScore, wordResult, _audioQualityMetrics, storePlaySoundSessionSoundObject, soundObj;

    return _regeneratorRuntime.wrap(function listenForPhraseSaga$(_context10) {
      while (1) {
        switch (_context10.prev = _context10.next) {
          case 0:
            _action$payload = action.payload, phrases = _action$payload.phrases, promptId = _action$payload.promptId, duration = _action$payload.duration, useLFOP = _action$payload.useLFOP;
            _context10.next = 3;
            return select(selectors.isFakeSRE);

          case 3:
            isFake = _context10.sent;

            if (!isFake) {
              _context10.next = 6;
              break;
            }

            return _context10.abrupt("return");

          case 6:
            newSessionId = uuidV4();
            _context10.next = 9;
            return clearCurrentSession(newSessionId);

          case 9:
            if (!(nextSessionId !== newSessionId)) {
              _context10.next = 11;
              break;
            }

            return _context10.abrupt("return", null);

          case 11:
            phraseSession = null;
            _context10.prev = 12;
            _context10.next = 15;
            return ensureSpeechEngineAndMicrophone(speechEngine, action);

          case 15:

            // Use "Listen for one phrase" session type, if phrases array only has one phrase
            // or fallback to "Listen For Phrases" session type
            if (useLFOP && phrases.length === 1 && speechEngine.createListenForOnePhraseSession) {
              phraseSession = speechEngine.createListenForOnePhraseSession(phrases[0]);
            } else {
              phraseSession = speechEngine.createListenForPhrasesSession(phrases);
            }
            setCurrentSession(phraseSession, "listenForPhrasesSession_" + newSessionId);
            phraseSession.withScoreThreshold(action.payload.scoreThreshold);

            if (!appSelectors.getSoundVolume) {
              _context10.next = 24;
              break;
            }

            _context10.next = 21;
            return select(appSelectors.getSoundVolume);

          case 21:
            _context10.t0 = _context10.sent;
            _context10.next = 25;
            break;

          case 24:
            _context10.t0 = 1;

          case 25:
            volume = _context10.t0;

            phraseSession.withMetadata(sonic.globals.getAUDIO_METADATA_ENERGY());
            phraseSession.withDefaultPromptSound(volume);
            if (duration) {
              phraseSession.withDuration(duration);
            }
            phraseSession.run();

            _context10.next = 32;
            return listenSession(phraseSession);

          case 32:

            // determine if "silence" is a result of getting no microphone input
            audioQualityMetrics = phraseSession.getFinalResult() ? phraseSession.getAudioQuality() : undefined;
            calibrationResult = audioQualityMetrics && audioQualityMetrics.getCalibration_result(); // NOTE: `calibrationResult` is a bad name for what this is

            silence = calibrationResult && calibrationResult === sonic.globals.getCALIBRATION_RESULT_NO_SIGNAL(); // i.e. 3 OR "no signal"

            tooLoud = calibrationResult && calibrationResult === sonic.globals.getCALIBRATION_RESULT_TOO_LOUD(); // i.e. 3 OR "no signal"

            tooSoft = calibrationResult && calibrationResult === sonic.globals.getCALIBRATION_RESULT_TOO_SOFT(); // i.e. 2 OR "no signal"

            snrEstimate = audioQualityMetrics && audioQualityMetrics.snr_estimate; // Get the signal to noise ratio

            tooMuchBackgroundNoise = snrEstimate < 15.0 && !silence; // with silence theres essentially no signal to calculate an SNR

            hypothesis = phraseSession.getHypothesis();
            grading = hypothesis.getGrading();
            detectedPhrase = phraseSession.getFinalResult() ? phraseSession.getFinalResult().response : "";

            // Different SRE session types have different calls to get the results.

            passed = phraseSession.getPassed && phraseSession.getPassed() || phraseSession.getFinalResult && phraseSession.getFinalResult().passed;

            if (!grading) {
              _context10.next = 55;
              break;
            }

            result = phraseSession.getFinalResult ? phraseSession.getFinalResult() : phraseSession.getResult();
            phraseListeningScore = grading.getScore();
            wordResult = result.wordResult || result.wordFinalResult;

            if (!(phraseSession.getStopReason() === sonic.globals.getSTOP_REASON_NONE())) {
              _context10.next = 52;
              break;
            }

            _context10.next = 50;
            return put(actions.listenForPhraseResult(promptId, 0, false, silence, false, false));

          case 50:
            _context10.next = 55;
            break;

          case 52:
            _audioQualityMetrics = phraseSession.getFinalResult() ? phraseSession.getAudioQuality() : undefined;
            _context10.next = 55;
            return put(actions.listenForPhraseResult(promptId, phraseListeningScore, false, silence, passed, false, wordResult, _audioQualityMetrics, result.saveSoundLogID, tooLoud, tooSoft, tooMuchBackgroundNoise, detectedPhrase));

          case 55:

            // Store the session sound object for playback in PlaySound sessions.
            storePlaySoundSessionSoundObject = SrePlaySound.actions.storePlaySoundSessionSoundObject;
            soundObj = phraseSession.detachSoundObject();

            if (!(!silence && soundObj)) {
              _context10.next = 60;
              break;
            }

            _context10.next = 60;
            return put(storePlaySoundSessionSoundObject(soundObj, phraseSession.getSoundLog()));

          case 60:
            _context10.prev = 60;

            if (!(phraseSession && phraseSession.getState() === sonic.Task.getRUNNING())) {
              _context10.next = 67;
              break;
            }

            logger.warn("ReactSRE: Forcing a listen session to end since we are exiting listenForPhraseSaga");
            _context10.next = 65;
            return put(actions.cancelSRESession());

          case 65:
            _context10.next = 71;
            break;

          case 67:
            if (!(phraseSession && phraseSession.getState() === sonic.Task.getERRORED())) {
              _context10.next = 71;
              break;
            }

            logger.warn("ReactSRE: A sonic error has occurred", phraseSession.getErrorMessage() // TODO - listenForPhrasesSession doesn't actually exist here!
            );
            _context10.next = 71;
            return put(actions.listenForPhrasesError(phraseSession.getState(), phraseSession.getErrorMessage()));

          case 71:
            _context10.next = 73;
            return cancelled();

          case 73:
            if (!_context10.sent) {
              _context10.next = 78;
              break;
            }

            _context10.next = 76;
            return put(actions.cancelSRESession());

          case 76:
            _context10.next = 78;
            return put(actions.listenForPhraseResult(promptId, 0, true, false));

          case 78:
            return _context10.finish(60);

          case 79:
          case "end":
            return _context10.stop();
        }
      }
    }, _marked10, this, [[12,, 60, 79]]);
  }

  /**
   * playSoundSessionSaga() starts a 'Sonic PlaySound' session.
   */
  function playSoundSessionSaga(action) {
    var newSessionId, getPlaySoundSessionSoundObj, playSoundSessionEnd, soundObj, startTimeMS, newSession, _eventChannel, _ref2, _type;

    return _regeneratorRuntime.wrap(function playSoundSessionSaga$(_context11) {
      while (1) {
        switch (_context11.prev = _context11.next) {
          case 0:
            newSessionId = uuidV4();
            _context11.next = 3;
            return clearCurrentSession(newSessionId);

          case 3:
            if (!(nextSessionId !== newSessionId)) {
              _context11.next = 5;
              break;
            }

            return _context11.abrupt("return", null);

          case 5:
            getPlaySoundSessionSoundObj = SrePlaySound.selectors.getPlaySoundSessionSoundObj;
            playSoundSessionEnd = SrePlaySound.actions.playSoundSessionEnd;
            _context11.next = 9;
            return select(getPlaySoundSessionSoundObj);

          case 9:
            soundObj = _context11.sent;

            if (!soundObj) {
              _context11.next = 46;
              break;
            }

            startTimeMS = action.payload.startTimeMS;
            newSession = speechEngine.createPlaySoundSession(soundObj);

            setCurrentSession(newSession, "playSoundSession_" + newSessionId);
            startTimeMS && newSession.withStartTimeMS(startTimeMS);
            _eventChannel = createEventChannel(newSession, "update", "end", "error", "start");

            newSession.run();
            _context11.prev = 17;

          case 18:
            if (!true) {
              _context11.next = 40;
              break;
            }

            _context11.next = 21;
            return take(_eventChannel);

          case 21:
            _ref2 = _context11.sent;
            _type = _ref2.type;

            if (!(_type === "status" || _type === "update")) {
              _context11.next = 28;
              break;
            }

            _context11.next = 26;
            return put(SrePlaySound.actions.playSoundSessionProgress(newSession));

          case 26:
            _context11.next = 38;
            break;

          case 28:
            if (!(_type === "start")) {
              _context11.next = 31;
              break;
            }

            _context11.next = 38;
            break;

          case 31:
            if (!(_type === "end")) {
              _context11.next = 34;
              break;
            }

            _context11.next = 38;
            break;

          case 34:
            if (!(_type === "error")) {
              _context11.next = 38;
              break;
            }

            logger.error("ReactSRE: playSoundSessionSaga error.", "playsound", _eventChannel);
            _context11.next = 38;
            return false;

          case 38:
            _context11.next = 18;
            break;

          case 40:
            _context11.prev = 40;
            _context11.next = 43;
            return put(playSoundSessionEnd(newSession, false));

          case 43:
            return _context11.finish(40);

          case 44:
            _context11.next = 47;
            break;

          case 46:
            logger.warn("ReactSRE: Call to start Sonic's PlaySound session but no sound object exists in state.");

          case 47:
          case "end":
            return _context11.stop();
        }
      }
    }, _marked11, this, [[17,, 40, 44]]);
  }

  /**
   * whileReadingTrackerSessionIsRunning() runs in sagas after the start of particular session types.
   * Listens for updates from the speech engine and dispatches progress data to the model.
   * Initiated in sagas for session types not requiring custome data from a speech session's onStatus() event.
   */
  function whileReadingTrackerSessionIsRunning(trackerSession) {
    var _SreReadingTracker$ac, readingTrackerSessionProgressV2, readingTrackerSessionResultV2, readingTrackerSessionStoreSound, currentWordIndex, eventChannel, _ref3, _type2, updatedCurrentWordIndex, canceled, silenceDetected, finalResult, gradedWords, finalScore, hypothesis, error, soundObj;

    return _regeneratorRuntime.wrap(function whileReadingTrackerSessionIsRunning$(_context12) {
      while (1) {
        switch (_context12.prev = _context12.next) {
          case 0:
            _SreReadingTracker$ac = SreReadingTracker.actions, readingTrackerSessionProgressV2 = _SreReadingTracker$ac.readingTrackerSessionProgressV2, readingTrackerSessionResultV2 = _SreReadingTracker$ac.readingTrackerSessionResultV2, readingTrackerSessionStoreSound = _SreReadingTracker$ac.readingTrackerSessionStoreSound;
            currentWordIndex = null;
            eventChannel = createEventChannel(trackerSession, "update", "end", "error", "start");
            _context12.prev = 3;

          case 4:
            if (!true) {
              _context12.next = 43;
              break;
            }

            _context12.next = 7;
            return take(eventChannel);

          case 7:
            _ref3 = _context12.sent;
            _type2 = _ref3.type;

            if (!(_type2 === "status" || _type2 === "update")) {
              _context12.next = 17;
              break;
            }

            updatedCurrentWordIndex = extractPath(trackerSession, "readingtrackersession.window.curr.word_index");

            if (!(currentWordIndex !== updatedCurrentWordIndex)) {
              _context12.next = 15;
              break;
            }

            currentWordIndex = updatedCurrentWordIndex;
            _context12.next = 15;
            return put(readingTrackerSessionProgressV2({
              currentSpeakingWordIndex: updatedCurrentWordIndex,
              isResultAvailable: extractPath(trackerSession, "readingtrackersession.isResultAvailable")
            }));

          case 15:
            _context12.next = 41;
            break;

          case 17:
            if (!(_type2 === "start")) {
              _context12.next = 20;
              break;
            }

            _context12.next = 41;
            break;

          case 20:
            if (!(_type2 === "end")) {
              _context12.next = 40;
              break;
            }

            if (!(trackerSession && trackerSession.getFinalResult())) {
              _context12.next = 38;
              break;
            }

            canceled = false;
            silenceDetected = !trackerSession.getHypothesis().getResponse();
            finalResult = trackerSession.getFinalResult();
            gradedWords = finalResult.words;
            finalScore = finalResult.score.finalScore;
            hypothesis = extractPath(trackerSession, "listensession.hypothesis.xmlString");
            error = ![sonic.Task.getSUCCEEDED(), sonic.Task.getINTERRUPTED()].includes(trackerSession.getState());
            _context12.next = 31;
            return put(readingTrackerSessionResultV2({
              finalResultExists: true,
              silenceDetected: silenceDetected,
              error: error,
              canceled: canceled,
              gradedWords: gradedWords,
              finalScore: finalScore,
              hypothesis: hypothesis
            }));

          case 31:
            if (!error) {
              _context12.next = 34;
              break;
            }

            logger.error("RSpeech: ReadingTracker session error.", trackerSession.getErrorMessage(), finalResult);
            return _context12.abrupt("return", null);

          case 34:
            // collect sound object / sound log
            // const silenceDetected = !trackerSession.getHypothesis().getResponse();
            soundObj = trackerSession.detachSoundObject();

            if (!(!silenceDetected && soundObj)) {
              _context12.next = 38;
              break;
            }

            _context12.next = 38;
            return put(readingTrackerSessionStoreSound(soundObj, trackerSession.getSoundLog()));

          case 38:
            _context12.next = 41;
            break;

          case 40:
            if (_type2 === "error") {
              logger.error("ReactSRE: whileReadingTrackerSessionIsRunning error.", eventChannel);
            }

          case 41:
            _context12.next = 4;
            break;

          case 43:
            _context12.prev = 43;
            _context12.next = 46;
            return clearCurrentSession();

          case 46:
            return _context12.finish(43);

          case 47:
          case "end":
            return _context12.stop();
        }
      }
    }, _marked12, this, [[3,, 43, 47]]);
  }

  /**
   * readingTrackerSagaV2 starts a 'ReadingTracker' session,
   *  listens for 'status' updates, and dispatches session results to the store.
   */
  function readingTrackerSagaV2(action) {
    var newSessionId, _action$payload2, sessionText, beginOfSpeechTimeout, endOfSpeechTimeout, promptSound, useGrammar, trackerSession;

    return _regeneratorRuntime.wrap(function readingTrackerSagaV2$(_context13) {
      while (1) {
        switch (_context13.prev = _context13.next) {
          case 0:
            _context13.next = 2;
            return select(selectors.isFakeSRE);

          case 2:
            if (!_context13.sent) {
              _context13.next = 4;
              break;
            }

            return _context13.abrupt("return");

          case 4:
            if (action.payload.text) {
              _context13.next = 7;
              break;
            }

            logger.warn("RSpeech: readingTrackerSagaV2() required 'text' is missing from action payload.", action.payload);
            return _context13.abrupt("return");

          case 7:
            newSessionId = uuidV4();
            _context13.next = 10;
            return clearCurrentSession(newSessionId);

          case 10:
            if (!(nextSessionId !== newSessionId)) {
              _context13.next = 12;
              break;
            }

            return _context13.abrupt("return", null);

          case 12:
            _context13.prev = 12;
            _context13.next = 15;
            return ensureSpeechEngineAndMicrophone(speechEngine, action);

          case 15:

            // Setup and run a reading tracker session
            _action$payload2 = action.payload, sessionText = _action$payload2.text, beginOfSpeechTimeout = _action$payload2.beginOfSpeechTimeout, endOfSpeechTimeout = _action$payload2.endOfSpeechTimeout, promptSound = _action$payload2.promptSound, useGrammar = _action$payload2.useGrammar;
            trackerSession = speechEngine.createReadingTrackerSession(sessionText, useGrammar === true);

            setCurrentSession(trackerSession, "readingTrackerSession_" + newSessionId);
            trackerSession.withMetadata(sonic.globals.getAUDIO_METADATA_ENERGY());
            beginOfSpeechTimeout && trackerSession.withBeginOfSpeechTimeout(beginOfSpeechTimeout);
            endOfSpeechTimeout && trackerSession.withEndOfSpeechTimeout(endOfSpeechTimeout);
            promptSound !== false && trackerSession.withDefaultPromptSound();
            trackerSession.run();

            // run loop to gather progress and end results from SRE session events
            _context13.next = 25;
            return call(whileReadingTrackerSessionIsRunning, trackerSession);

          case 25:
            _context13.prev = 25;
            _context13.next = 28;
            return cancelled();

          case 28:
            if (!_context13.sent) {
              _context13.next = 33;
              break;
            }

            _context13.next = 31;
            return put(SreReadingTracker.actions.readingTrackerSessionResultV2({ canceled: true }));

          case 31:
            _context13.next = 33;
            return clearCurrentSession();

          case 33:
            return _context13.finish(25);

          case 34:
          case "end":
            return _context13.stop();
        }
      }
    }, _marked13, this, [[12,, 25, 34]]);
  }

  function readingTrackerPlaybackSagaV2(action) {
    var newSessionId, startTime, _SreReadingTracker$se, getTrackerSoundObj, readingTrackerSessionText, readingTrackerHypothesis, soundObj, trackerText, hypothesis, newSession, _eventChannel2, event;

    return _regeneratorRuntime.wrap(function readingTrackerPlaybackSagaV2$(_context14) {
      while (1) {
        switch (_context14.prev = _context14.next) {
          case 0:
            newSessionId = uuidV4();
            _context14.next = 3;
            return clearCurrentSession(newSessionId);

          case 3:
            if (!(nextSessionId !== newSessionId)) {
              _context14.next = 5;
              break;
            }

            return _context14.abrupt("return", null);

          case 5:
            startTime = action.payload.startTime;
            _SreReadingTracker$se = SreReadingTracker.selectors, getTrackerSoundObj = _SreReadingTracker$se.getTrackerSoundObj, readingTrackerSessionText = _SreReadingTracker$se.readingTrackerSessionText, readingTrackerHypothesis = _SreReadingTracker$se.readingTrackerHypothesis;
            _context14.next = 9;
            return select(getTrackerSoundObj);

          case 9:
            soundObj = _context14.sent;
            _context14.next = 12;
            return select(readingTrackerSessionText);

          case 12:
            trackerText = _context14.sent;
            _context14.next = 15;
            return select(readingTrackerHypothesis);

          case 15:
            hypothesis = _context14.sent;

            if (!(trackerText && soundObj && hypothesis)) {
              _context14.next = 42;
              break;
            }

            _context14.prev = 17;
            newSession = speechEngine.createReadingTrackerPlaybackSession(trackerText, soundObj, hypothesis);

            setCurrentSession(newSession, "readingTrackerPlayback_" + newSessionId);
            newSession.withStartTimeMS(startTime);
            newSession.run();
            _eventChannel2 = createEventChannel(newSession, "update", "end", "error", null, "interrupting");

          case 23:
            if (!true) {
              _context14.next = 31;
              break;
            }

            _context14.next = 26;
            return take(_eventChannel2);

          case 26:
            event = _context14.sent;
            _context14.next = 29;
            return fork(handleReadingTrackerPlaybackEvent, newSession, event);

          case 29:
            _context14.next = 23;
            break;

          case 31:
            _context14.next = 36;
            break;

          case 33:
            _context14.prev = 33;
            _context14.t0 = _context14["catch"](17);

            logger.error("ReactSRE: ReadingTrackerPlayback saga error.", _context14.t0);

          case 36:
            _context14.prev = 36;
            _context14.next = 39;
            return clearCurrentSession();

          case 39:
            return _context14.finish(36);

          case 40:
            _context14.next = 44;
            break;

          case 42:
            logger.warn("ReactSRE: readingTrackerPlaybackSagaV2 required values are invalid.");
            logger.warn("trackerText", trackerText, "hypothesis", hypothesis, "soundObj", soundObj);

          case 44:
          case "end":
            return _context14.stop();
        }
      }
    }, _marked14, this, [[17, 33, 36, 40]]);
  }

  function handleReadingTrackerPlaybackEvent(session, event) {
    var readingTrackerPlaybackProgress, update, wordStartIndex, wordEndIndex, currentTime, progress;
    return _regeneratorRuntime.wrap(function handleReadingTrackerPlaybackEvent$(_context15) {
      while (1) {
        switch (_context15.prev = _context15.next) {
          case 0:
            readingTrackerPlaybackProgress = SreReadingTracker.actions.readingTrackerPlaybackProgress;

            if (!(event.type === "status")) {
              _context15.next = 11;
              break;
            }

            update = extractPath(session, "readingtrackersession.updateResult");
            wordStartIndex = update.wordStartIndex;
            wordEndIndex = wordStartIndex + update.wordLength - 1;
            currentTime = extractPath(session, "readingtrackerplaybacksession.currentTimeMS");
            progress = _extends({}, update, {
              currentTime: currentTime,
              wordStartIndex: wordStartIndex,
              wordEndIndex: wordEndIndex
            });
            _context15.next = 9;
            return put(readingTrackerPlaybackProgress(progress, currentTime));

          case 9:
            _context15.next = 21;
            break;

          case 11:
            if (!(event.type === "interrupting")) {
              _context15.next = 14;
              break;
            }

            _context15.next = 21;
            break;

          case 14:
            if (!(event.type === "end")) {
              _context15.next = 19;
              break;
            }

            _context15.next = 17;
            return put(SreReadingTracker.actions.readingTrackerPlaybackEnd());

          case 17:
            _context15.next = 21;
            break;

          case 19:
            if (!(event.type === "error")) {
              _context15.next = 21;
              break;
            }

            throw event;

          case 21:
          case "end":
            return _context15.stop();
        }
      }
    }, _marked15, this);
  }

  function ensureSpeechEngineAndMicrophone(engine, action) {
    return _regeneratorRuntime.wrap(function ensureSpeechEngineAndMicrophone$(_context16) {
      while (1) {
        switch (_context16.prev = _context16.next) {
          case 0:
            if (engine) {
              _context16.next = 7;
              break;
            }

            _context16.next = 3;
            return initializeSRESaga(action);

          case 3:
            _context16.next = 5;
            return take("INIT_SRE_SUCCESS");

          case 5:
            _context16.next = 7;
            return setCurrentMic();

          case 7:
          case "end":
            return _context16.stop();
        }
      }
    }, _marked16, this);
  }

  /**
   * SRE doesn't have implement seek functionality.
   * Instead interrupt (stop) and startWithTimeMS is used to achieve seek effect.
   */
  function seekTrackerPlaybackSessionTime() {
    var startTime;
    return _regeneratorRuntime.wrap(function seekTrackerPlaybackSessionTime$(_context17) {
      while (1) {
        switch (_context17.prev = _context17.next) {
          case 0:
            _context17.next = 2;
            return select(SreReadingTracker.getPlaybackCurrentTime);

          case 2:
            startTime = _context17.sent;
            _context17.next = 5;
            return call(readingTrackerPlaybackSagaV2, { payload: { startTime: startTime } });

          case 5:
          case "end":
            return _context17.stop();
        }
      }
    }, _marked17, this);
  }

  function installMicSaga(action) {
    var micToInstall, micDevice;
    return _regeneratorRuntime.wrap(function installMicSaga$(_context18) {
      while (1) {
        switch (_context18.prev = _context18.next) {
          case 0:
            micToInstall = action.payload.micToInstall;
            // set it in the SRE engine

            _context18.next = 3;
            return logger.log("ReactSRE: setting mic to ", micToInstall);

          case 3:
            micDevice = speechEngine.getAudioEnumerator().openInputDevice(micToInstall);

            speechEngine.setMicrophone(micDevice);
            // micChosen sets in the store as selectedMicrophone so app can see
            //  and also allows app to catch the action and do what it wants to
            _context18.next = 7;
            return put(actions.micChosen(micToInstall));

          case 7:
          case "end":
            return _context18.stop();
        }
      }
    }, _marked18, this);
  }

  /**
   * setCurrentMic() looks at what mic the app wants, and what mics are available.
   * It installs the app-requested mic if it is available,
   * or the default mic if the app mic is not available or not specified.
   * It only installs the mic if it is not already installed.
   *
   * setCurrentMic() also populates the store member `availableMicrophones`
   * which is exposed to the app by the `sreMicList` selector
   */
  function setCurrentMic() {
    var appMic, micList, targetMic, lastSelectedMic;
    return _regeneratorRuntime.wrap(function setCurrentMic$(_context19) {
      while (1) {
        switch (_context19.prev = _context19.next) {
          case 0:
            if (!appSelectors.getCurrentMicrophone) {
              _context19.next = 6;
              break;
            }

            _context19.next = 3;
            return select(appSelectors.getCurrentMicrophone);

          case 3:
            _context19.t0 = _context19.sent;
            _context19.next = 7;
            break;

          case 6:
            _context19.t0 = null;

          case 7:
            appMic = _context19.t0;
            _context19.next = 10;
            return refreshMicList();

          case 10:
            _context19.next = 12;
            return select(selectors.sreMicList);

          case 12:
            micList = _context19.sent;
            targetMic = void 0;
            // see if the mic the app wants is in the list

            if (appMic) {
              targetMic = micList.find(function (m) {
                return m === appMic;
              });
            }
            // if there was no app-chosen mic OR if it is not currently available,
            //  try to use the default mic (first in the list)
            if (!targetMic) {
              targetMic = micList[0];
            }
            // what was the mic we chose most recently?  from the react-sre lib store
            _context19.next = 18;
            return select(selectors.sreSelectedMicrophone);

          case 18:
            lastSelectedMic = _context19.sent;

            if (!(targetMic && targetMic !== lastSelectedMic)) {
              _context19.next = 22;
              break;
            }

            _context19.next = 22;
            return call(installMicSaga, { payload: { micToInstall: targetMic } });

          case 22:
          case "end":
            return _context19.stop();
        }
      }
    }, _marked19, this);
  }

  function pollForMicAccess() {
    var micAccess;
    return _regeneratorRuntime.wrap(function pollForMicAccess$(_context20) {
      while (1) {
        switch (_context20.prev = _context20.next) {
          case 0:
            if (!true) {
              _context20.next = 15;
              break;
            }

            _context20.next = 3;
            return checkMicAccessSaga();

          case 3:
            micAccess = _context20.sent;

            logger.info("Current mic access:", micAccess);

            if (!(micAccess !== "denied")) {
              _context20.next = 11;
              break;
            }

            _context20.next = 8;
            return refreshMicList();

          case 8:
            _context20.next = 10;
            return put(actions.userEnabledMicAccess());

          case 10:
            return _context20.abrupt("return");

          case 11:
            _context20.next = 13;
            return delay(1000);

          case 13:
            _context20.next = 0;
            break;

          case 15:
          case "end":
            return _context20.stop();
        }
      }
    }, _marked20, this);
  }

  /**
   * Runs through a flow to check mic access.
   *  - Check for access
   *  - If access, return.
   */
  function micAccessFlow() {
    var micAccess, pollTask, acknowledgedBrowserMicBlockResult;
    return _regeneratorRuntime.wrap(function micAccessFlow$(_context21) {
      while (1) {
        switch (_context21.prev = _context21.next) {
          case 0:
            _context21.next = 2;
            return checkMicAccessSaga();

          case 2:
            micAccess = _context21.sent;

            if (!(micAccess !== "denied")) {
              _context21.next = 7;
              break;
            }

            _context21.next = 6;
            return put(actions.confirmMicAccess());

          case 6:
            return _context21.abrupt("return");

          case 7:
            _context21.next = 9;
            return fork(pollForMicAccess);

          case 9:
            pollTask = _context21.sent;
            _context21.next = 12;
            return put(actions.showBrowserMicBlockModal());

          case 12:
            _context21.next = 14;
            return take(actions.SRE_USER_ACKNOWLEDGES_BROWSER_MIC_BLOCK_MODAL);

          case 14:
            acknowledgedBrowserMicBlockResult = _context21.sent.payload.choice;

            if (!(acknowledgedBrowserMicBlockResult === actions.ACKNOWLEDGED_MIC_BLOCKED)) {
              _context21.next = 23;
              break;
            }

            logger.warn("ReactSRE: Microphone denied in browser by user, and user acknowledged. Disabling speech.");
            _context21.next = 19;
            return put(actions.disableSpeech());

          case 19:
            _context21.next = 21;
            return put(actions.closeCalibrateWindow());

          case 21:
            _context21.next = 27;
            break;

          case 23:
            _context21.next = 25;
            return put(actions.enableSpeech());

          case 25:
            _context21.next = 27;
            return put(actions.confirmMicAccess());

          case 27:

            pollTask.cancel();

          case 28:
          case "end":
            return _context21.stop();
        }
      }
    }, _marked21, this);
  }

  function initializeSonic(action) {
    return _regeneratorRuntime.wrap(function initializeSonic$(_context22) {
      while (1) {
        switch (_context22.prev = _context22.next) {
          case 0:
            if (!speechEngine) {
              _context22.next = 2;
              break;
            }

            return _context22.abrupt("return");

          case 2:

            logger.log("ReactSRE: loadSonicLib", _JSON$stringify(action.payload, null, 2));
            _context22.next = 5;
            return loadSonicLib(action.payload && action.payload.srePath, action.payload && action.payload.sonicWorkerUrl);

          case 5:

            logger.log("ReactSRE: initializeSRE");
            speechEngine = createSpeechEngine();
            _context22.next = 9;
            return put(actions.setVersion(speechEngine.getVersion()));

          case 9:
            _context22.next = 11;
            return put(actions.sonicLoadComplete());

          case 11:
          case "end":
            return _context22.stop();
        }
      }
    }, _marked22, this);
  }

  function initializeSpeechModel() {
    var targetLanguage, targetVoiceModel, targetDifficulty, lastVoiceModelLoaded, modelId;
    return _regeneratorRuntime.wrap(function initializeSpeechModel$(_context23) {
      while (1) {
        switch (_context23.prev = _context23.next) {
          case 0:
            _context23.next = 2;
            return select(appSelectors.getCurrentProductLanguage);

          case 2:
            targetLanguage = _context23.sent;
            _context23.next = 5;
            return select(appSelectors.getSpeechVoiceType);

          case 5:
            targetVoiceModel = _context23.sent;

            if (!appSelectors.getSpeechDifficulty) {
              _context23.next = 12;
              break;
            }

            _context23.next = 9;
            return select(appSelectors.getSpeechDifficulty);

          case 9:
            _context23.t0 = _context23.sent;
            _context23.next = 13;
            break;

          case 12:
            _context23.t0 = DEFAULT_DIFFICULTY;

          case 13:
            targetDifficulty = _context23.t0;
            _context23.next = 16;
            return select(selectors.sreLastModelLoaded);

          case 16:
            lastVoiceModelLoaded = _context23.sent;
            modelId = targetLanguage + targetVoiceModel + targetDifficulty;

            if (!(modelId !== lastVoiceModelLoaded)) {
              _context23.next = 26;
              break;
            }

            logger.log("ReactSRE: loading speech model", targetLanguage, targetVoiceModel, targetDifficulty);
            _context23.next = 22;
            return put(actions.sreBeginLoading());

          case 22:
            _context23.next = 24;
            return loadModelSession(speechEngine, targetLanguage, targetVoiceModel, targetDifficulty);

          case 24:
            _context23.next = 27;
            break;

          case 26:
            logger.log("ReactSRE: speech model already loaded");

          case 27:
            _context23.next = 29;
            return put(actions.speechModelLoadComplete(modelId));

          case 29:
          case "end":
            return _context23.stop();
        }
      }
    }, _marked23, this);
  }

  /**
   * Saga to initialize the SRE library.
   */
  function initializeSRESaga() {
    var action = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    return _regeneratorRuntime.wrap(function initializeSRESaga$(_context24) {
      while (1) {
        switch (_context24.prev = _context24.next) {
          case 0:
            logger.log("ReactSRE: initializeSRESaga");

            if (!loading) {
              _context24.next = 3;
              break;
            }

            return _context24.abrupt("return", null);

          case 3:
            loading = true;

            if (speechEngine) {
              _context24.next = 7;
              break;
            }

            _context24.next = 7;
            return initializeSonic(action);

          case 7:
            _context24.next = 9;
            return initializeSpeechModel();

          case 9:
            _context24.next = 11;
            return put(actions.onSRESuccess());

          case 11:
            loading = false;

          case 12:
          case "end":
            return _context24.stop();
        }
      }
    }, _marked24, this);
  }

  function cancelSessionSaga() {
    return _regeneratorRuntime.wrap(function cancelSessionSaga$(_context25) {
      while (1) {
        switch (_context25.prev = _context25.next) {
          case 0:
            _context25.next = 2;
            return clearCurrentSession(null);

          case 2:
            _context25.next = 4;
            return true;

          case 4:
          case "end":
            return _context25.stop();
        }
      }
    }, _marked25, this);
  }

  function setCurrentSession(newSession, sessionId) {
    currentSession = newSession;
    currentSessionId = sessionId;
  }

  function sreSaga() {
    return _regeneratorRuntime.wrap(function sreSaga$(_context26) {
      while (1) {
        switch (_context26.prev = _context26.next) {
          case 0:
            _context26.next = 2;
            return takeLatest(actions.INIT_SRE, initializeSRESaga);

          case 2:
            _context26.next = 4;
            return takeEvery(actions.SRE_LISTEN_FOR_PHRASES, cancelableSaga(listenForPhraseSaga, cancelListenActions, "listenForPhraseSaga"));

          case 4:
            _context26.next = 6;
            return takeEvery(SreReadingTracker.READING_TRACKER_SESSION_START_V2, cancelableSaga(readingTrackerSagaV2, cancelListenActions, "readingTrackerSagaV2"));

          case 6:
            _context26.next = 8;
            return takeLatest(SreReadingTracker.READING_TRACKER_PLAYBACK_START_V2, readingTrackerPlaybackSagaV2);

          case 8:
            _context26.next = 10;
            return takeLatest(SreReadingTracker.READING_TRACKER_PLAYBACK_INTERRUPT, cancelSessionSaga);

          case 10:
            _context26.next = 12;
            return takeFirst(SreReadingTracker.READING_TRACKER_PLAYBACK_SEEK_TIME, seekTrackerPlaybackSessionTime);

          case 12:
            _context26.next = 14;
            return takeLatest(SrePlaySound.PLAY_SOUND_SESSION_START, cancelableSaga(playSoundSessionSaga, cancelListenActions, "playSoundSessionSaga"));

          case 14:
            _context26.next = 16;
            return takeEvery(actions.SRE_CALIBRATE_START, cancelableSaga(calibrateSaga, [actions.SRE_CANCEL_SESSION], "calibrateSaga"));

          case 16:
            _context26.next = 18;
            return takeEvery(actions.SRE_CHECK_MIC, checkMicAccessSaga);

          case 18:
            _context26.next = 20;
            return takeLatest(actions.SRE_CANCEL_SESSION, cancelSessionSaga);

          case 20:
            _context26.next = 22;
            return takeEvery(actions.SRE_LOAD_SPEECH_MODEL, initializeSpeechModel);

          case 22:
            _context26.next = 24;
            return takeEvery(actions.SRE_LOAD_SONIC_LIBRARY, initializeSonic);

          case 24:
            _context26.next = 26;
            return takeEvery(actions.SRE_REFRESH_MIC_LIST, refreshMicList);

          case 26:
            _context26.next = 28;
            return takeEvery(actions.SRE_OPEN_CALIBRATE_WIN, setCurrentMic);

          case 28:
            _context26.next = 30;
            return takeEvery(actions.SRE_SET_CURRENT_MIC, setCurrentMic);

          case 30:
            _context26.next = 32;
            return takeEvery(actions.SRE_INSTALL_MIC, installMicSaga);

          case 32:
            _context26.next = 34;
            return takeEvery(actions.SRE_START_MIC_CHECK_FLOW, micAccessFlow);

          case 34:
            _context26.next = 36;
            return takeEvery(actions.ENABLE_AUDIO_LOG, enableAudioLog);

          case 36:
          case "end":
            return _context26.stop();
        }
      }
    }, _marked26, this);
  }

  return sreSaga;
}

export default createSRESaga;